import React from "react";
import { Navigate, useParams } from "react-router-dom";
import SEOHead from "../../components/tools/SEOHead";
import MarketingShell, { Arrow } from "../MarketingShell";
import {
  NewsAnnouncement,
  NewsEdition,
  NewsGlanceItem,
  NewsOfficialEvent,
  NewsTopItem,
  decisionLabels,
  directorySources,
  editionByDate,
  editionStats,
  formatEventDate,
  latestEdition,
  nicheLabel,
  officialEventsFor,
  platformLabels,
  plainText,
  skippedSourceCount,
  sourcesByTreatment,
  treatmentGroups,
  weeklyCoverage,
} from "./newsData";
import { readAnnouncementSeed } from "./newsX";
import "./NewsPage.css";

const subscribeUrl = (process.env.REACT_APP_NEWS_SUBSCRIBE_URL || "").trim();
const subscribeEmail = (process.env.REACT_APP_NEWS_SUBSCRIBE_EMAIL || "news@youwo.ai").trim();
const subscribeProvider = (process.env.REACT_APP_NEWS_SUBSCRIBE_PROVIDER || "").trim().toLowerCase();

function Marked({ text }: { text: string }) {
  return <>{text.split(/(==[^=]+==)/g).map((part, index) => (
    part.startsWith("==") && part.endsWith("==")
      ? <span className="news-hl" key={index}>{part.slice(2, -2)}</span>
      : <React.Fragment key={index}>{part}</React.Fragment>
  ))}</>;
}

function SourceLink({ source }: { source: NewsTopItem["source"] }) {
  return (
    <a className="news-source" href={source.url} target="_blank" rel="noopener noreferrer">
      {source.name} <Arrow diagonal />
    </a>
  );
}

function VideoThumb({ item }: { item: NewsTopItem }) {
  const video = item.video!;
  const [playing, setPlaying] = React.useState(false);
  const [failed, setFailed] = React.useState(false);
  const [posterFailed, setPosterFailed] = React.useState(false);
  if (failed) {
    return (
      <p className="news-video-note">
        Not on our player yet. <a href={item.source.url} target="_blank" rel="noopener noreferrer">Watch on {item.source.name}</a>
      </p>
    );
  }
  if (playing) {
    return (
      <video
        className="news-video"
        controls
        autoPlay
        playsInline
        poster={posterFailed ? undefined : video.poster}
        src={video.src}
        onError={() => setFailed(true)}
      />
    );
  }
  return (
    <button type="button" className="news-thumb" onClick={() => setPlaying(true)} aria-label={`Play: ${plainText(item.title)}`}>
      {video.poster && !posterFailed
        ? <img src={video.poster} alt="" loading="lazy" decoding="async" onError={() => setPosterFailed(true)} />
        : <span className="news-thumb-fallback" aria-hidden="true">{item.source.name}</span>}
      <span className="news-play" aria-hidden="true" />
      {video.duration && <span className="news-duration">{video.duration}</span>}
    </button>
  );
}

function TopItem({ item, index }: { item: NewsTopItem; index: number }) {
  return (
    <article className="news-item" id={item.id}>
      <p className="news-item-meta">
        <span className="news-index">{String(index + 1).padStart(2, "0")}</span>
        {item.source.favorite && <span className="news-fav" title="From a favorite" aria-label="Favorite" />}
        <span>{item.source.name}</span>
        <span className="news-muted">· {item.video ? "Watch" : "Read"} {item.minutes} min</span>
      </p>
      <h2><Marked text={item.title} /></h2>
      {item.video && <VideoThumb item={item} />}
      <ul className="news-points">{item.points.map((point) => <li key={point}>{point}</li>)}</ul>
      {item.more.length > 0 && (
        <details className="news-more">
          <summary>Read more</summary>
          {item.more.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
          <SourceLink source={item.source} />
        </details>
      )}
    </article>
  );
}

function GlanceItem({ item }: { item: NewsGlanceItem }) {
  return (
    <li>
      <details className="news-glance-item" id={item.id}>
        <summary>
          <span className="news-glance-title"><Marked text={item.title} /></span>
          <span className="news-muted">{item.source.name}</span>
        </summary>
        <ul className="news-points">{item.points.map((point) => <li key={point}>{point}</li>)}</ul>
        <SourceLink source={item.source} />
      </details>
    </li>
  );
}

function Announcements({ items }: { items: NewsAnnouncement[] }) {
  if (items.length === 0) return null;
  return (
    <section className="news-x" aria-labelledby="news-x-title">
      <h2 id="news-x-title">On X</h2>
      <ul>
        {items.map((item) => (
          <li key={item.id} id={item.id}>
            <a className="news-source" href={item.url} target="_blank" rel="noopener noreferrer">
              @{item.handle} <Arrow diagonal />
            </a>
            <p className="news-x-text">{item.text}</p>
            <p className="news-muted">{item.why}</p>
          </li>
        ))}
      </ul>
    </section>
  );
}

function OfficialEvents({ edition }: { edition: NewsEdition }) {
  const events = officialEventsFor(edition.date);
  if (events.length === 0) return null;
  return (
    <section className="news-events" aria-labelledby="news-events-title">
      <h2 id="news-events-title">Official events</h2>
      <ol>
        {events.map((event) => <EventRow event={event} key={event.id} />)}
      </ol>
    </section>
  );
}

function EventRow({ event }: { event: NewsOfficialEvent }) {
  return (
    <li id={event.id}>
      <time dateTime={event.date}>{formatEventDate(event)}</time>
      <a href={event.url} target="_blank" rel="noopener noreferrer">
        {event.name} <Arrow diagonal />
      </a>
      <span className="news-muted">{event.where}</span>
      <p>{event.why}</p>
    </li>
  );
}

function WeekStrip({ edition }: { edition: NewsEdition }) {
  const topics = weeklyCoverage(edition);
  return (
    <section className="news-week" aria-label="Topics covered this week">
      <span className="news-muted">This week</span>
      <ul>
        {topics.map((topic) => (
          <li key={topic.topic} className={topic.covered ? "is-covered" : undefined}>
            {topic.label}<span className="news-sr">{topic.covered ? " covered" : " not yet"}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function NewsSubscribe() {
  const fields = (
    <div className="news-subscribe-row">
      <label className="news-sr" htmlFor="news-email">Email</label>
      <input id="news-email" name="email" type="email" required autoComplete="email" placeholder="you@example.com" />
      {subscribeProvider === "buttondown" && <input type="hidden" name="embed" value="1" />}
      <button className="quant-button" type="submit">Subscribe</button>
    </div>
  );
  const formProps = subscribeUrl
    ? { action: subscribeUrl, method: "post" }
    : { action: `mailto:${subscribeEmail}?subject=${encodeURIComponent("Subscribe to YouWo News")}`, method: "post", encType: "text/plain" };
  return (
    <form className="news-subscribe" id="subscribe" {...formProps}>
      <h2>Three a day, by email.</h2>
      {fields}
    </form>
  );
}

function Edition({ edition, canonical }: { edition: NewsEdition; canonical: string }) {
  const stats = editionStats(edition);
  const lead = plainText(edition.top[0]?.title ?? "YouWo News");
  const summary = `${stats.minutes} min · ${stats.videos} videos · ${stats.sources} sources`;
  return (
    <MarketingShell active="news">
      <SEOHead
        title={`${lead} — YouWo News`}
        description={edition.top.map((item) => plainText(item.title)).join(" ")}
        canonicalUrl={canonical}
        ogType="article"
        ogImage={edition.top.find((item) => item.video?.poster)?.video?.poster}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: lead,
          datePublished: edition.date,
          inLanguage: "en",
          mainEntityOfPage: `https://youwo.ai/news/${edition.date}`,
          publisher: { "@type": "Organization", name: "YouWo", url: "https://youwo.ai" },
        }}
      />
      <main id="main-content" tabIndex={-1} className="quant-wrap news" lang="en">
        <header className="news-heading">
          <h1>Today’s three</h1>
          <p className="news-muted">{summary}</p>
        </header>
        {edition.top.map((item, index) => <TopItem item={item} index={index} key={item.id} />)}
        {edition.glance.length > 0 && (
          <section className="news-glance" aria-labelledby="news-glance-title">
            <h2 id="news-glance-title">Glance</h2>
            <ul>{edition.glance.map((item) => <GlanceItem item={item} key={item.id} />)}</ul>
          </section>
        )}
        <Announcements items={readAnnouncementSeed(edition)} />
        <OfficialEvents edition={edition} />
        <WeekStrip edition={edition} />
        <NewsSubscribe />
        <a className="news-footer-link" href="/news/sources">Sources we follow <Arrow /></a>
      </main>
    </MarketingShell>
  );
}

function MissingEdition() {
  React.useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex";
    document.head.appendChild(meta);
    return () => { meta.remove(); };
  }, []);
  return (
    <MarketingShell active="news">
      <SEOHead title="Edition not found — YouWo News" description="Read today’s YouWo News." />
      <main id="main-content" tabIndex={-1} className="quant-wrap news" lang="en">
        <h1>That edition isn’t here.</h1>
        <a className="quant-text-link" href="/news">Today’s three <Arrow /></a>
      </main>
    </MarketingShell>
  );
}

export function NewsHomePage() {
  const edition = latestEdition();
  if (!edition) return <MissingEdition />;
  return <Edition edition={edition} canonical="https://youwo.ai/news" />;
}

export function NewsTodayPage() {
  const edition = latestEdition();
  if (!edition) return <Navigate to="/news" replace />;
  return <Navigate to={`/news/${edition.date}`} replace />;
}

export function NewsEditionPage() {
  const { date } = useParams<{ date: string }>();
  const edition = editionByDate(date);
  if (!edition) return <MissingEdition />;
  return <Edition edition={edition} canonical={`https://youwo.ai/news/${edition.date}`} />;
}

export function NewsSourcesPage() {
  return (
    <MarketingShell active="news">
      <SEOHead
        title="Sources — YouWo News"
        description="The channels YouWo News draws from, sorted by how much reader time each may take."
        canonicalUrl="https://youwo.ai/news/sources"
        structuredData={{ "@context": "https://schema.org", "@type": "CollectionPage", name: "YouWo News sources" }}
      />
      <main id="main-content" tabIndex={-1} className="quant-wrap news" lang="en">
        <a className="news-back" href="/news">← Today’s three</a>
        <header className="news-heading">
          <h1>Sources</h1>
          <p className="news-muted">{directorySources.length} channels · {skippedSourceCount} skipped · <span className="news-fav" aria-hidden="true" />keep</p>
          <nav className="news-jumps" aria-label="Source groups">
            {treatmentGroups.map((group) => (
              <a href={`#${group.treatment}`} key={group.treatment}>{group.title} {sourcesByTreatment(group.treatment).length}</a>
            ))}
          </nav>
        </header>
        {treatmentGroups.map((group) => (
          <section className="news-directory-group" id={group.treatment} key={group.treatment} aria-labelledby={`${group.treatment}-title`}>
            <h2 id={`${group.treatment}-title`}>{group.title}</h2>
            <p className="news-muted">{group.description}</p>
            <ul className="news-directory">
              {sourcesByTreatment(group.treatment).map((source) => (
                <li key={source.id}>
                  <div className="news-directory-top">
                    <a href={source.url} target="_blank" rel="noopener noreferrer">
                      {source.decision === "keep" && <span className="news-fav" aria-label="Keep" />}{source.name} <Arrow diagonal />
                    </a>
                    <span className={`news-status news-status-${source.decision}`}>{decisionLabels[source.decision]}</span>
                  </div>
                  <p className="news-muted">{platformLabels[source.platform]} · {nicheLabel(source.niche)}</p>
                  <p>{source.why}</p>
                  {source.gate && <p className="news-caveat">{source.gate}</p>}
                </li>
              ))}
            </ul>
          </section>
        ))}
      </main>
    </MarketingShell>
  );
}
